import React, { createContext, useState, useEffect, useContext } from 'react';
import User from '../models/User_b';
import Profile from '../models/Profile_b';
import Settings from '../models/Settings_b';
import { defaultUserController } from '../controllers/UserController';
import apiService from '../services/APIService';
import TokenExpirationModal from '../components/TokenExpirationModal';
import { SERVER_STATUS } from '../modules/Enumeration';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Helper: Parse JWT to get expiry
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    token: null,
    refreshToken: null,
    user: null,
    userModel: null,
    profile: null,
    settings: null,
    isLoading: true,
    isSignedIn: false,
    tokenExpiry: null,
    lastRefresh: null
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimerRef = React.useRef(null);

  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);
  const [pendingReauth, setPendingReauth] = useState(false);
  const [tokenExpiredDetails, setTokenExpiredDetails] = useState(null);
  const authStateRef = React.useRef(authState);
  const authFailureHandledRef = React.useRef(false);

  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  useEffect(() => {
    apiService.setStatusCallback((status, details) => {
      if (status !== SERVER_STATUS.ERROR_AUTH) return;
      const signedIn = Boolean(authStateRef.current?.isSignedIn);
      if (!signedIn) return;
      if (authFailureHandledRef.current) return;

      authFailureHandledRef.current = true;
      setTokenExpiredDetails(details || null);
      setPendingReauth(true);
      setShowTokenExpiredModal(true);
    });
  }, []);

  // Check if token is expired or about to expire (within 5 minutes)
  const isTokenExpired = () => {
    if (!authState.tokenExpiry) return true;
    const expiryDate = new Date(authState.tokenExpiry);
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    return expiryDate <= fiveMinutesFromNow;
  };

  // Handle token refresh with retry logic
  const refreshAuthToken = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    if (isRefreshing) {
      console.log('Token refresh already in progress, waiting...');
      return null;
    }

    // Check for refresh token from both state and localStorage
    const currentRefreshToken = authState.refreshToken || localStorage.getItem('@refresh_token');
    
    if (!currentRefreshToken) {
      console.warn('No refresh token available, skipping refresh');
      return null;
    }

    try {

      setIsRefreshing(true);

      const response = await apiService.post(
        '/auth/refresh-token',
        {},
        { token: currentRefreshToken }
      );

      if (response && response.data && response.data.accessToken) {
        const newToken = response.data.accessToken;
        const newRefreshToken = response.data.refreshToken || currentRefreshToken;
        
        const tokenData = parseJwt(newToken);
        const tokenExpiry = tokenData ? new Date(tokenData.exp * 1000) : null;
        
        localStorage.setItem('@auth_token', newToken);
        localStorage.setItem('@refresh_token', newRefreshToken);
        if (tokenExpiry) {
          localStorage.setItem('@token_expiry', tokenExpiry.toISOString());
        }
        
        setAuthState(prevState => ({
          ...prevState,
          token: newToken,
          refreshToken: newRefreshToken,
          tokenExpiry,
          lastRefresh: new Date()
        }));
        
        apiService.setAuthToken(newToken);
        setIsRefreshing(false);
        
        scheduleTokenRefresh(tokenExpiry);
        
        console.log('Token refreshed successfully');
        return newToken;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      setIsRefreshing(false);
      console.error('Token refresh failed:', error);
      
      // Don't retry if there's no refresh token or it's a 401 error
      if (retryCount < MAX_RETRIES && error.status !== 401 && currentRefreshToken) {
        console.log(`Retrying token refresh (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return refreshAuthToken(retryCount + 1);
      }
      
      // Only sign out if we actually have a session to sign out from
      if (authState.isSignedIn && currentRefreshToken && error.status === 401) {
        console.error('Token refresh failed with 401, prompting reauth');
        setPendingReauth(true);
        setShowTokenExpiredModal(true);
      }
      return null;
    }
  };

  // Schedule automatic token refresh before expiry
  const scheduleTokenRefresh = React.useCallback((tokenExpiry) => {
    // Clear any existing refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!tokenExpiry) return;

    const expiryDate = new Date(tokenExpiry);
    const now = new Date();
    const timeUntilExpiry = expiryDate.getTime() - now.getTime();
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes
    const refreshTime = timeUntilExpiry - refreshBuffer;

    // Only schedule if token has more than 10 minutes left
    if (refreshTime > 10 * 60 * 1000) {
      console.log(`Scheduling token refresh in ${Math.floor(refreshTime / 1000 / 60)} minutes`);
      refreshTimerRef.current = setTimeout(() => {
        // Double-check we're not already refreshing and token still exists
        const hasRefreshToken = localStorage.getItem('@refresh_token');
        if (hasRefreshToken && !isRefreshing) {
          console.log('Automatic token refresh triggered');
          refreshAuthToken();
        } else {
          console.log('Automatic token refresh cancelled - already refreshing or no refresh token');
        }
      }, refreshTime);
    } else if (timeUntilExpiry > 2 * 60 * 1000 && !isRefreshing) {
      // Only refresh immediately if token has more than 2 minutes left and not already refreshing
      console.log('Token expiring soon, refreshing immediately');
      refreshAuthToken();
    }
  }, [isRefreshing]);

  const handleTokenExpiredModalClose = () => {
    setShowTokenExpiredModal(false);
    if (pendingReauth) {
      setPendingReauth(false);
      Promise.resolve()
        .then(async () => {
          await signOut();
        })
        .finally(() => {
          authFailureHandledRef.current = false;
          if (typeof window !== 'undefined') {
            window.location.assign('/');
          }
        });
    }
  };

  // Load token and user data from localStorage on app start
  useEffect(() => {
    const loadAuthState = async () => {
      try {
        const token = localStorage.getItem('@auth_token');
        const refreshToken = localStorage.getItem('@refresh_token');
        const userData = localStorage.getItem('@user_data');
        const userModelData = localStorage.getItem('@user_model');
        const profileData = localStorage.getItem('@user_profile');
        const settingsData = localStorage.getItem('@user_settings');
        const tokenExpiry = localStorage.getItem('@token_expiry');
        
        if (token && userData) {
          const parsedUserData = JSON.parse(userData);
          const parsedUserModel = userModelData ? JSON.parse(userModelData) : null;
          const parsedProfile = profileData ? JSON.parse(profileData) : null;
          const parsedSettings = settingsData ? JSON.parse(settingsData) : null;
          const expiryDate = tokenExpiry ? new Date(tokenExpiry) : null;
          
          if (expiryDate && expiryDate <= new Date()) {
            console.log('Token expired, attempting refresh...');
            const newToken = await refreshAuthToken();
            if (!newToken) {
              setAuthState({
                token: null,
                refreshToken: null,
                user: null,
                userModel: null,
                profile: null,
                settings: null,
                isLoading: false,
                isSignedIn: false,
                tokenExpiry: null,
                lastRefresh: null
              });
              return;
            }
          } else {
            scheduleTokenRefresh(expiryDate);
          }
          
          setAuthState({
            token,
            refreshToken,
            user: parsedUserData,
            userModel: parsedUserModel ? User.fromObject(parsedUserModel) : null,
            profile: parsedProfile ? Profile.fromAPI(parsedProfile) : null,
            settings: parsedSettings ? Settings.fromAPI(parsedSettings) : null,
            isLoading: false,
            isSignedIn: true,
            tokenExpiry: expiryDate,
            lastRefresh: new Date()
          });
          
          apiService.setAuthToken(token);

          authFailureHandledRef.current = false;
          setTokenExpiredDetails(null);
          refreshUser().catch(err => {
            console.error('Failed to refresh user data:', err);
          });
        } else {
          setAuthState({
            token: null,
            refreshToken: null,
            user: null,
            userModel: null,
            profile: null,
            settings: null,
            isLoading: false,
            isSignedIn: false,
            tokenExpiry: null,
            lastRefresh: null
          });
        }
      } catch (error) {
        console.error('Error loading auth state:', error);
        setAuthState({
          token: null,
          refreshToken: null,
          user: null,
          userModel: null,
          profile: null,
          settings: null,
          isLoading: false,
          isSignedIn: false,
          tokenExpiry: null,
          lastRefresh: null
        });
      }
    };
    
    loadAuthState();
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line
  }, []);

  // Refresh user data
  const refreshUser = async () => {
    try {
      if (isTokenExpired() && authState.refreshToken) {
        await refreshAuthToken();
      }
      const newUser = await defaultUserController.Me();
      const userToStore = {
        ...newUser,
        profile: newUser.profile || null,
        settings: newUser.settings || null
      };
      const userModel = User.fromObject(userToStore);
      const profile = userToStore.profile;
      const settings = userToStore.settings;
      localStorage.setItem('@user_data', JSON.stringify(userToStore));
      localStorage.setItem('@user_model', JSON.stringify(userModel));
      if (profile) localStorage.setItem('@user_profile', JSON.stringify(profile));
      if (settings) localStorage.setItem('@user_settings', JSON.stringify(settings));
      setAuthState(prevState => ({
        ...prevState,
        user: userToStore,
        userModel: userModel,
        profile: profile,
        settings: settings,
        lastRefresh: new Date()
      }));
      return userToStore;
    } catch (err) {
      if (err.status === 401 && authState.refreshToken) {
        if (!authFailureHandledRef.current) {
          authFailureHandledRef.current = true;
          setTokenExpiredDetails({ status: 401, message: 'Token refresh failed', reason: 'refresh_failed' });
          setPendingReauth(true);
          setShowTokenExpiredModal(true);
        }
        throw err;
      } else {
        throw err;
      }
    }
  };

  // Refresh all data
  const refreshData = async () => {
    try {
      if (isTokenExpired() && authState.refreshToken) {
        await refreshAuthToken();
      }
      const newUser = await refreshUser();
      return newUser;
    } catch (err) {
      throw err;
    }
  };

  // Sign in function
  const signIn = async (token, refreshToken) => {
    try {
      const now = new Date();
      let tokenExpiry = null;
      const tokenData = parseJwt(token);
      
      if (tokenData && tokenData.exp) {
        tokenExpiry = new Date(tokenData.exp * 1000);
      } else {
        tokenExpiry = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      }
      
      localStorage.setItem('@auth_token', token);
      localStorage.setItem('@refresh_token', refreshToken);
      localStorage.setItem('@token_expiry', tokenExpiry.toISOString());
      
      apiService.setAuthToken(token);
      authFailureHandledRef.current = false;
      setTokenExpiredDetails(null);

      let cachedUser = null;
      try {
        const cached = localStorage.getItem('@user_data');
        cachedUser = cached ? JSON.parse(cached) : null;
      } catch (e) {
        cachedUser = null;
      }

      setAuthState(prevState => ({
        ...prevState,
        token,
        refreshToken,
        user: cachedUser,
        userModel: cachedUser ? User.fromObject(cachedUser) : null,
        profile: cachedUser?.profile || null,
        settings: cachedUser?.settings || null,
        isLoading: false,
        isSignedIn: true,
        tokenExpiry,
        lastRefresh: now
      }));
      
      scheduleTokenRefresh(tokenExpiry);

      refreshUser().catch(() => {
      });
      
      console.log('Sign in successful, token expires at:', tokenExpiry);
      return true;
    } catch (error) {
      console.error('Error signing in:', error);

      // Ensure we do not leave the UI stuck in a loading state.
      localStorage.removeItem('@auth_token');
      localStorage.removeItem('@refresh_token');
      localStorage.removeItem('@token_expiry');
      localStorage.removeItem('@user_data');
      localStorage.removeItem('@user_model');
      localStorage.removeItem('@user_profile');
      localStorage.removeItem('@user_settings');

      apiService.setAuthToken(null);

      setAuthState(prevState => ({
        ...prevState,
        token: null,
        refreshToken: null,
        user: null,
        userModel: null,
        profile: null,
        settings: null,
        isLoading: false,
        isSignedIn: false,
        tokenExpiry: null,
        lastRefresh: null
      }));

      return false;
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      
      if (authState.token && !isTokenExpired()) {
        try {
          await apiService.post('/auth/logout', { refreshToken: authState.refreshToken });
        } catch (logoutError) {
          console.warn('Server logout failed, continuing with local logout:', logoutError);
        }
      }
      
      localStorage.removeItem('@auth_token');
      localStorage.removeItem('@refresh_token');
      localStorage.removeItem('@token_expiry');
      localStorage.removeItem('@user_data');
      localStorage.removeItem('@user_model');
      localStorage.removeItem('@user_profile');
      localStorage.removeItem('@user_settings');
      
      setAuthState({
        token: null,
        refreshToken: null,
        user: null,
        userModel: null,
        profile: null,
        settings: null,
        isLoading: false,
        isSignedIn: false,
        tokenExpiry: null,
        lastRefresh: null
      });
      
      apiService.setAuthToken(null);
      authFailureHandledRef.current = false;
      setTokenExpiredDetails(null);
      setIsRefreshing(false);
      
      console.log('Sign out successful');
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      return false;
    }
  };

  // Update user data
  const updateUser = async (userData) => {
    try {
      if (isTokenExpired() && authState.refreshToken) {
        await refreshAuthToken();
      }
      const updatedUser = { ...authState.user, ...userData };
      const userModel = User.fromObject(updatedUser);
      let profile = authState.profile;
      if (userData.profile) {
        profile = Profile.fromAPI({ ...authState.profile, ...userData.profile });
        updatedUser.profile = profile;
      }
      let settings = authState.settings;
      if (userData.settings) {
        settings = Settings.fromAPI({ ...authState.settings, ...userData.settings });
        updatedUser.settings = settings;
      }
      localStorage.setItem('@user_data', JSON.stringify(updatedUser));
      localStorage.setItem('@user_model', JSON.stringify(userModel));
      if (profile) localStorage.setItem('@user_profile', JSON.stringify(profile));
      if (settings) localStorage.setItem('@user_settings', JSON.stringify(settings));
      setAuthState(prevState => ({
        ...prevState,
        user: updatedUser,
        userModel: userModel,
        profile: profile,
        settings: settings,
        lastRefresh: new Date()
      }));
      return true;
    } catch (error) {
      console.error('Error updating user data:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        signIn,
        signOut,
        updateUser,
        refreshUser,
        refreshData,
        isTokenExpired,
        refreshAuthToken
      }}
    >
      {children}
      <TokenExpirationModal 
        open={showTokenExpiredModal} 
        onClose={handleTokenExpiredModalClose}
        details={tokenExpiredDetails}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;
